import React, { useState, useEffect } from 'react';
import './UserOnboarding.css';
import { API_BASE_URL } from '../config';

interface UserOnboardingProps {
  user: any;
  onComplete: () => void;
}

// Helper function to calculate baby's age in weeks from birth date
const calculateAgeWeeks = (birthDateString: string): number | null => {
  if (!birthDateString) return null;

  const birthDate = new Date(birthDateString);
  const today = new Date();
  const ageDays = Math.floor((today.getTime() - birthDate.getTime()) / (1000 * 60 * 60 * 24));
  return Math.floor(ageDays / 7);
};

// Helper function to get typical nap range for a given age in months
const getTypicalNapRange = (ageMonths: number): { min: number; max: number } | null => {
  if (ageMonths < 0) return null;

  // Use 12-month range for babies older than 12 months
  const lookupMonth = ageMonths > 12 ? 12 : ageMonths;

  const napRanges: { [key: number]: { min: number; max: number } } = {
    0: { min: 4, max: 6 },   // 0-2 months
    1: { min: 4, max: 6 },
    2: { min: 4, max: 6 },
    3: { min: 3, max: 4 },   // 3-5 months
    4: { min: 3, max: 4 },
    5: { min: 3, max: 4 },
    6: { min: 2, max: 3 },   // 6-8 months
    7: { min: 2, max: 3 },
    8: { min: 2, max: 3 },
    9: { min: 2, max: 2 },   // 9-12 months
    10: { min: 2, max: 2 },
    11: { min: 2, max: 2 },
    12: { min: 1, max: 2 },  // 12+ months
  };

  return napRanges[lookupMonth] || { min: 1, max: 2 };
};

const UserOnboarding: React.FC<UserOnboardingProps> = ({ user, onComplete }) => {
  // Check which flow the user came from
  const showInviteInput = sessionStorage.getItem('show_invite_input') === 'true';
  const showCreateBaby = sessionStorage.getItem('show_create_baby') === 'true';

  // Determine initial mode based on flags
  let initialMode: 'choose' | 'create' | 'join' | 'sleep-preferences' = 'choose';
  if (showCreateBaby) {
    initialMode = 'create';
  } else if (showInviteInput) {
    initialMode = 'join';
  }

  const [mode, setMode] = useState<'choose' | 'create' | 'join' | 'sleep-preferences'>(initialMode);

  // Clear the flags when component mounts
  useEffect(() => {
    if (showInviteInput) {
      sessionStorage.removeItem('show_invite_input');
    }
    if (showCreateBaby) {
      sessionStorage.removeItem('show_create_baby');
    }
  }, [showInviteInput, showCreateBaby]);
  const [babyName, setBabyName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Sleep preferences state
  const [createdBabyId, setCreatedBabyId] = useState<string>('');
  const [targetNapCount, setTargetNapCount] = useState<number | null>(null);
  const [preferredBedtime, setPreferredBedtime] = useState<string>('');
  const [preferredWakeTime, setPreferredWakeTime] = useState<string>('');

  const createBaby = async () => {
    if (!babyName.trim()) {
      setError('Please enter a baby name');
      return;
    }

    if (!birthDate) {
      setError('Please select a birth date');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/babies/?user_id=${user.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: babyName.trim(),
          birth_date: birthDate,
        }),
      });

      if (response.ok) {
        const babyData = await response.json();
        setCreatedBabyId(babyData.id);
        // Skip sleep preferences for now - go straight to completion
        // To re-enable: change onComplete() back to setMode('sleep-preferences')
        onComplete();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to create baby');
      }
    } catch (error) {
      setError('Failed to create baby');
    } finally {
      setIsSubmitting(false);
    }
  };

  const joinWithCode = async () => {
    if (!inviteCode.trim() || inviteCode.length !== 14) {
      setError('Please enter a valid 14-digit invite code');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/links/accept?user_id=${user.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code: inviteCode.trim(),
        }),
      });

      if (response.ok) {
        onComplete();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to accept invite');
      }
    } catch (error) {
      setError('Failed to accept invite');
    } finally {
      setIsSubmitting(false);
    }
  };

  const saveSleepPreferences = async () => {
    // Check if any field is filled
    const hasPreferences = targetNapCount !== null || preferredBedtime || preferredWakeTime;

    if (!hasPreferences) {
      // Nothing to save, just complete onboarding
      onComplete();
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/sleep-preferences/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          baby_id: createdBabyId,
          target_nap_count: targetNapCount,
          preferred_bedtime: preferredBedtime || null,
          preferred_wake_time: preferredWakeTime || null,
        }),
      });

      if (response.ok) {
        onComplete();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to save preferences');
      }
    } catch (error) {
      setError('Failed to save preferences');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (mode === 'choose') {
    return (
      <div className="onboarding-container">
        <div className="onboarding-content">
          <h1>Welcome to Baby Sleep Tracker!</h1>
          <p className="welcome-message">
            Hi {user.username}! To get started, please choose one of the options below:
          </p>

          <div className="onboarding-options">
            <div className="option-card" onClick={() => setMode('create')}>
              <div className="option-icon">👶</div>
              <h3>Create a Baby</h3>
              <p>Start tracking sleep for your baby</p>
            </div>

            <div className="option-card" onClick={() => setMode('join')}>
              <div className="option-icon">👥</div>
              <h3>Join with Invite Code</h3>
              <p>Use a code to track sleep together with someone else</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (mode === 'create') {
    return (
      <div className="onboarding-container">
        <div className="onboarding-content">
          {/* Only show back button if user came from choice screen, not from direct flow */}
          {!showCreateBaby && (
            <button className="back-btn" onClick={() => setMode('choose')}>
              ← Back
            </button>
          )}

          <h1>Create Your Baby</h1>
          <p>Enter your baby's name to start tracking sleep:</p>

          {error && <div className="error-message">{error}</div>}

          <div className="input-section">
            <input
              type="text"
              placeholder="Baby's name"
              value={babyName}
              onChange={(e) => setBabyName(e.target.value)}
              disabled={isSubmitting}
              maxLength={100}
              autoFocus
            />

            <div className="form-group">
              <label htmlFor="birthDate">Birth Date</label>
              <input
                type="date"
                id="birthDate"
                value={birthDate}
                onChange={(e) => setBirthDate(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
                disabled={isSubmitting}
                required
              />
            </div>

            <div className="button-group">
              <button
                onClick={createBaby}
                className="primary-btn"
                disabled={isSubmitting || !babyName.trim() || !birthDate}
              >
                {isSubmitting ? 'Creating...' : 'Create Baby'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (mode === 'join') {
    return (
      <div className="onboarding-container">
        <div className="onboarding-content">
          {/* Only show back button if user came from choice screen, not from direct flow */}
          {!showInviteInput && (
            <button className="back-btn" onClick={() => setMode('choose')}>
              ← Back
            </button>
          )}

          <h1>Join with Invite Code</h1>
          <p>Enter the 14-digit code you received to start tracking sleep together:</p>

          {error && <div className="error-message">{error}</div>}

          <div className="input-section">
            <input
              type="text"
              placeholder="Enter 14-digit code"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value.replace(/\D/g, '').slice(0, 14))}
              disabled={isSubmitting}
              maxLength={14}
              autoFocus
            />

            <div className="button-group">
              <button
                onClick={joinWithCode}
                className="primary-btn"
                disabled={isSubmitting || inviteCode.length !== 14}
              >
                {isSubmitting ? 'Joining...' : 'Join'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (mode === 'sleep-preferences') {
    // Calculate baby age and typical nap range for guidance
    const babyAgeWeeks = calculateAgeWeeks(birthDate);
    const ageMonths = babyAgeWeeks !== null ? Math.floor(babyAgeWeeks / 4) : null;
    const napRange = ageMonths !== null ? getTypicalNapRange(ageMonths) : null;

    return (
      <div className="onboarding-container">
        <div className="onboarding-content">
          <button className="back-btn" onClick={() => setMode('create')}>
            ← Back
          </button>

          <h1>Set Sleep Goals (Optional)</h1>
          <p>These goals help AI predictions adapt to your routine. You can change these anytime.</p>

          {error && <div className="error-message">{error}</div>}

          <div className="input-section">
            <div className="form-group">
              <label htmlFor="nap-count">Target naps per day</label>
              <p style={{ fontSize: '0.9em', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
                Setting target naps per day is optional, but setting it will help us suggest when your little one may sleep
              </p>
              <select
                id="nap-count"
                value={targetNapCount === null ? '' : targetNapCount}
                onChange={(e) => setTargetNapCount(e.target.value === '' ? null : parseInt(e.target.value))}
                disabled={isSubmitting}
              >
                <option value="">No preference</option>
                <option value="0">0 naps</option>
                <option value="1">1 nap</option>
                <option value="2">2 naps</option>
                <option value="3">3 naps</option>
                <option value="4">4 naps</option>
                <option value="5">5 naps</option>
              </select>
              {babyAgeWeeks !== null && napRange !== null && (
                <p style={{ marginTop: '8px', fontSize: '0.9em', fontStyle: 'italic', color: '#666' }}>
                  A baby at {babyAgeWeeks} weeks normally has between {napRange.min} to {napRange.max} naps
                </p>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="bedtime">Preferred bedtime</label>
              <input
                type="time"
                id="bedtime"
                value={preferredBedtime}
                onChange={(e) => setPreferredBedtime(e.target.value)}
                disabled={isSubmitting}
              />
            </div>

            <div className="form-group">
              <label htmlFor="wake-time">Preferred wake time</label>
              <input
                type="time"
                id="wake-time"
                value={preferredWakeTime}
                onChange={(e) => setPreferredWakeTime(e.target.value)}
                disabled={isSubmitting}
              />
            </div>

            <div className="button-group">
              <button
                onClick={saveSleepPreferences}
                className="primary-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Saving...' : 'Save & Continue'}
              </button>

              <button
                onClick={onComplete}
                className="secondary-btn"
                disabled={isSubmitting}
              >
                Skip for now
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default UserOnboarding;